import type { CSSProperties } from 'react';

interface FieldProps {
    label: string;
    value?: string | number | null;
    /** Monospace the value — for ids, where character-level accuracy matters. */
    mono?: boolean;
}

/**
 * One read-only label/value row on the profile.
 *
 * Renders an em dash for anything absent rather than collapsing the row. Most
 * of the new CRM fields are unset for most users, and a row that vanishes when
 * empty makes the page reflow differently per user and reads as missing UI
 * rather than as missing data.
 *
 * On layout: this row carries long, unbroken values — emails, Stripe customer
 * ids, LinkedIn URLs — and it used to overflow its card on a narrow screen. Two
 * things are load-bearing:
 *
 *   · `min-width: 0` on the value. A flex item defaults to `min-width: auto`,
 *     which refuses to shrink below its content, so `text-break` alone does
 *     nothing and the text simply pushes out of the card.
 *   · `flex-wrap` on the row, so when the value genuinely cannot fit beside its
 *     label the two stack instead of colliding. The value stays right-aligned
 *     from `sm` up, and goes left-aligned once stacked — right-aligned text
 *     under a left-aligned label reads as a mistake.
 */
const valueStyle = (mono?: boolean): CSSProperties => ({
    minWidth: 0,
    ...(mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : null),
});

const Field = ({ label, value, mono }: FieldProps) => (
    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2 gap-2 gap-sm-3">
        <span className="text-muted text-nowrap">{label}</span>
        <span className="text-break text-start text-sm-end" style={valueStyle(mono)}>
            {value === undefined || value === null || value === '' ? '—' : value}
        </span>
    </div>
);

export default Field;
